import { useLogging } from '@ecosystem/newron-client-lib';
import { actions, elements } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';

import { useInsightRenderer } from '../../private/hooks';
import { useCollectionAnalytics } from '../../shared/analytics';
import { AddInsightToCollectionsModal } from '../../shared/components';
import {
  AddInsightToCollectionsActionProps,
  AddInsightToCollectionsActionType,
  CollectionDataType,
  InsightInfo,
} from '../../shared/types';
import { logWrapper } from '../../shared/utils';

const { Menu, Button } = actions;
const { Icon } = elements;

export const AddInsightToCollectionsAction = logWrapper(
  <TInsightData extends object, TViewState extends object>(
    props: AddInsightToCollectionsActionProps<TInsightData, TViewState>
  ): React.JSX.Element => {
    const {
      defaultDataType = 'static',
      createInsightDataFn,
      type,
      proposition,
      getTrackingTags,
      createViewStateFn,
      selectDataType,
      suggestedDescription,
      actionType,
      disabled = false,
    } = props;
    const [modalOpen, setModalOpen] = useState(false);
    const { trackAddInsightToCollectionButtonClicked } = useCollectionAnalytics();
    const [dataType, setDataType] = useState<CollectionDataType>(defaultDataType);
    const [insightData, setInsightData] = useState<TInsightData>();
    const [insightInfoLoaded, setInsightInfoLoaded] = useState<InsightInfo>();
    const { error: logError } = useLogging();
    const insightRenderer = useInsightRenderer(proposition, type);

    const getInsightData = useCallback(
      (selectedDataType: CollectionDataType) => {
        try {
          const createdInsightState = createInsightDataFn(selectedDataType);

          if (!createdInsightState) {
            throw new Error('createInsightDataFn failed to provide insight data.');
          }
          setInsightData(createdInsightState);
        } catch (error) {
          logError(error, { action: 'getInsightData', proposition, type });
        }
      },
      [createInsightDataFn, logError, proposition, type]
    );

    const trackOpenModal = useCallback(() => {
      trackAddInsightToCollectionButtonClicked({
        getTrackingTags,
        insightInfo: insightInfoLoaded,
      });
    }, [trackAddInsightToCollectionButtonClicked, getTrackingTags, insightInfoLoaded]);

    const onOpenModal = useCallback(async () => {
      try {
        if (!insightRenderer || !insightRenderer.getInsightInfo) {
          throw new Error(`No insight renderer found for proposition ${proposition} and type ${type}`);
        }
        const loadedInfo = await insightRenderer.getInsightInfo(insightData);
        setInsightInfoLoaded(loadedInfo);
        setModalOpen(true);
      } catch (err) {
        logError(err, { action: 'loadInsightInfoFromRegister', proposition, type });
      }
    }, [logError, insightRenderer, insightData, proposition, type]);

    const onCloseModal = useCallback(() => {
      setModalOpen(false);
      setDataType(defaultDataType);
    }, [defaultDataType]);

    useEffect(() => {
      getInsightData(dataType);
    }, [getInsightData, dataType]);

    useEffect(() => {
      if (insightInfoLoaded && modalOpen) {
        trackOpenModal();
      }
    }, [trackOpenModal, insightInfoLoaded, modalOpen]);

    return (
      <>
        {actionType === AddInsightToCollectionsActionType.Button ? (
          <Button
            onClick={onOpenModal}
            testId="add-insight-to-collections-button"
            mode="secondary"
            label="Add to collection"
            disabled={disabled}
          >
            <Button.Icon>
              <Button.Icon.icons.AddCollection />
            </Button.Icon>
          </Button>
        ) : (
          <Menu.Item onSelect={onOpenModal} isDisabled={disabled} testId="add-insight-to-collections-menu-item">
            <Icon>
              <Icon.icons.AddCollection />
            </Icon>
            Add to collection
          </Menu.Item>
        )}
        {typeof insightInfoLoaded?.chartName === 'string' && (
          <AddInsightToCollectionsModal
            insightInfo={insightInfoLoaded}
            dataType={dataType}
            insightData={insightData}
            description={suggestedDescription}
            isOpen={modalOpen}
            onClose={onCloseModal}
            proposition={proposition}
            selectDataType={selectDataType}
            setDataType={setDataType}
            getTrackingTags={getTrackingTags}
            type={type}
            createViewStateFn={createViewStateFn}
          />
        )}
      </>
    );
  },
  'AddInsightToCollectionsAction'
);
